import React, { useState } from 'react';
import { getAuthHeaders } from '../../../api/core.js';
import { CheckCircle, X, Mail, Download, ChevronDown, ChevronUp, Loader2, ClipboardCheck, AlertTriangle } from 'lucide-react';

/**
 * Email templates for vehicle entry workflow
 */
const VEHICLE_EMAIL_TEMPLATES = [
  {
    code: 'VEH_ACQUISITION',
    label: 'Confirmation acquisition',
    recipientType: 'manager',
    description: 'Informer l\'equipe de l\'ajout du vehicule'
  },
  {
    code: 'VEH_LEASING_CONFIRM',
    label: 'Confirmation loueur',
    recipientType: 'custom',
    placeholder: 'loueur@example.com',
    description: 'Confirmer la reception du vehicule au loueur'
  },
  {
    code: 'VEH_BROKER_NOTIFY',
    label: 'Notification courtier assurance',
    recipientType: 'broker',
    placeholder: 'courtier@example.com',
    description: 'Informer le courtier pour l\'ajout a l\'assurance flotte'
  }
];

/**
 * VehicleCreatedSuccessModal
 * Shown after successful vehicle creation with email generation options
 */
export default function VehicleCreatedSuccessModal({
  isOpen,
  onClose,
  vehicle,
  isEditMode = false,
  onStartInspection = null,
}) {
  const [showEmailActions, setShowEmailActions] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadedFiles, setDownloadedFiles] = useState([]);

  if (!isOpen || !vehicle) return null;

  const handleGenerateEmail = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      const recipient = recipientEmail || 'destinataire@example.com';
      const url = `/api/v1/email_templates/${selectedTemplate.code}/download/vehicle/${vehicle.id}?email=${encodeURIComponent(recipient)}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Erreur generation email');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${selectedTemplate.code}_${vehicle.id}.eml`;

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setDownloadedFiles(prev => [...prev, { template: selectedTemplate.code, filename }]);
      setSelectedTemplate(null);
      setRecipientEmail('');
    } catch (err) {
      console.error('Email generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditMode ? 'Vehicule Modifie' : 'Vehicule Ajoute'}
              </h2>
              <p className="text-sm text-gray-500">
                {vehicle.make} {vehicle.model} - {vehicle.license_plate}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Success Message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm">
              Le vehicule a ete {isEditMode ? 'modifie' : 'ajoute a la flotte'} avec succes.
              {!isEditMode && ' Vous pouvez maintenant generer des emails pour informer les parties prenantes.'}
            </p>
          </div>

          {/* Inspection Prompt — set initial resource levels */}
          {!isEditMode && (
            <div className="border border-blue-200 rounded-lg bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900 text-sm mb-1">Inspection d'entree recommandee</h4>
                  <p className="text-xs text-blue-700 mb-3">
                    Pour un suivi fiable des consommables (freins, pneus, niveaux, eclairage, carrosserie),
                    effectuez une inspection initiale du vehicule.
                  </p>
                  {onStartInspection ? (
                    <button
                      onClick={() => { onClose(); onStartInspection(vehicle); }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      Lancer l'inspection d'entree
                    </button>
                  ) : (
                    <p className="text-xs text-blue-600 italic">
                      Vous pourrez lancer une inspection depuis l'onglet Ressources du vehicule.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Email Actions (for new vehicles) */}
          {!isEditMode && (
            <div className="border border-amber-200 rounded-lg bg-amber-50 overflow-hidden">
              <button
                onClick={() => setShowEmailActions(!showEmailActions)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-100"
              >
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-900">Actions Email</span>
                </div>
                {showEmailActions ? (
                  <ChevronUp className="w-4 h-4 text-amber-600" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-amber-600" />
                )}
              </button>

              {showEmailActions && (
                <div className="px-4 pb-4 space-y-3">
                  <p className="text-sm text-amber-800">
                    Generez des emails .eml pour informer le loueur, courtier ou votre equipe.
                  </p>

                  {/* Template Selection */}
                  <div className="space-y-2">
                    {VEHICLE_EMAIL_TEMPLATES.map((template) => (
                      <button
                        key={template.code}
                        onClick={() => {
                          setSelectedTemplate(template);
                          setRecipientEmail('');
                        }}
                        className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                          selectedTemplate?.code === template.code
                            ? 'bg-amber-100 border-2 border-amber-400'
                            : 'bg-white border border-amber-200 hover:border-amber-300'
                        }`}
                      >
                        <Mail className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          selectedTemplate?.code === template.code ? 'text-amber-600' : 'text-gray-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-900 text-sm block">{template.label}</span>
                          <span className="text-xs text-gray-500">{template.description}</span>
                        </div>
                        {downloadedFiles.some(d => d.template === template.code) && (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Recipient Email Input */}
                  {selectedTemplate && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Email destinataire
                      </label>
                      <input
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder={selectedTemplate.placeholder || 'destinataire@example.com'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                      <button
                        onClick={handleGenerateEmail}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 text-sm"
                      >
                        {loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Telecharger {selectedTemplate.label}.eml
                      </button>
                    </div>
                  )}

                  {/* Downloaded Files */}
                  {downloadedFiles.length > 0 && (
                    <div className="text-xs text-green-700 bg-green-50 p-2 rounded">
                      {downloadedFiles.length} email(s) telecharge(s)
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
